import React, { useState, useEffect, useRef } from 'react';
import { Client } from '@twilio/conversations';

interface ChatRoomProps {
  token: string;
  roomName: string;
}

interface Message {
  author: string;
  body: string;
  timestamp: Date;
}

const ChatRoom: React.FC<ChatRoomProps> = ({ token, roomName }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [client, setClient] = useState<Client | null>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    const initChat = async () => {
      try {
        const client = new Client(token);
        await client.initialize();
        setClient(client);

        const conversation = await client.createConversation({
          uniqueName: roomName
        });
        await conversation.join();

        conversation.on('messageAdded', (message) => {
          setMessages(prev => [...prev, {
            author: message.author,
            body: message.body,
            timestamp: message.dateCreated
          }]);
        });
      } catch (error) {
        console.error('Error initializing chat:', error);
      }
    };

    initChat();
  }, [token, roomName]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !client) return;

    try {
      const conversation = await client.getConversationByUniqueName(roomName);
      await conversation.sendMessage(newMessage);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${
              message.author === client?.user.identity ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-xs px-4 py-2 rounded-lg ${
                message.author === client?.user.identity
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <p className="text-sm">{message.body}</p>
              <span className="text-xs opacity-75">
                {new Date(message.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="p-4 border-t">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Type a message..."
          />
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatRoom;
